import { useEffect, useMemo, useState } from "react";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { formatDateForApi } from "@/utils/format";
import { BANG_KEO_PAGE_LIMIT } from "@/contants";
import LtdLoading from "@/components/LichThiDauFull/LtdLoading";
import NoData from "@/components/NoData";
import DatePicker from "@/components/LichThiDauFull/DatePicker";
import LeagueFilter from "@/components/LichThiDauFull/LeagueFilter";
import { useCompConfig } from "@/context/CompConfigContext";
import { buildThemeConfig } from "@/components/LichThiDauFull/buildThemeConfig";
import SidebarMatch from "@/components/LichThiDauByLeagueSidebar/SidebarMatch";
import LeagueGroup from "@/components/BangKeoKetQuaSidebar/LeagueGroup";
import "@/components/LichThiDauFull/index.css";
import "@/components/LichThiDauByLeagueSidebar/index.css";
import "@/components/BangKeoKetQuaSidebar/index.css";

const getLeaguesFromData = (items = []) => {
  const seen = new Set();
  const leagues = [];
  for (const item of items) {
    const league = item?.league;
    if (!league) continue;
    const id = String(league.id || league.name || "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    leagues.push({
      id: league.id || league.name,
      name: league.name || league.nameVN,
      nameVN: league.nameVN,
      logo: league.logo,
      isHot: league.isHot,
    });
  }
  return leagues;
};

export default function LichThiDauFullSidebar({ type = "tatca" }) {
  const isKetqua = type === "ketqua";
  const compConfig = useCompConfig();
  const themeStyle = buildThemeConfig(compConfig, compConfig.hasDataConfig);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [selectedIds, setSelectedIds] = useState(null);
  const { clearAllData } = useLiveData();

  const dateString = formatDateForApi(selectedDate);

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      ...(isKetqua ? { status: "end" } : {}),
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, isKetqua],
  );

  const {
    leaguesWithMatches,
    loading,
    hasMore,
    loadMoreRef,
    isLoadingMore,
  } = useBangKeoMatches(queryObject, true);

  const groups = useMemo(() => {
    const raw = Array.isArray(leaguesWithMatches) ? leaguesWithMatches : [];
    return raw.map((item) => ({
      ...item,
      matches: Array.isArray(item?.matches)
        ? item.matches
        : Object.values(item?.matches || {}).filter(Boolean),
    }));
  }, [leaguesWithMatches]);

  const filteredMatches = useMemo(() => {
    if (selectedIds === null) return groups;
    return groups.filter((item) =>
      selectedIds.some(
        (id) => String(id) === String(item.league?.id || item.league?.name),
      ),
    );
  }, [groups, selectedIds]);

  const matchIds = useMemo(
    () =>
      filteredMatches.flatMap(
        (league) =>
          league.matches?.map((m) => Number(m.id)).filter(Number.isFinite) || [],
      ),
    [filteredMatches],
  );

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedIds(null);
  };

  const filterLeagues = useMemo(() => getLeaguesFromData(groups), [groups]);

  const handleLeagueToggle = (id) => {
    const nextId = String(id);
    setSelectedIds((prev) => {
      if (prev === null) return [nextId];
      const has = prev.some((x) => String(x) === nextId);
      const next = has ? prev.filter((x) => String(x) !== nextId) : [...prev, nextId];
      if (next.length === 0 || next.length === filterLeagues.length) return null;
      return next;
    });
  };

  return (
    <div className="kbv-ltd-full kbv-ltd-sb kbv-kq-sb" style={themeStyle}>
      <div className="kbv-ltd-sb-filters">
        <DatePicker
          compact
          labelToday="Hôm nay"
          initDate={selectedDate}
          direction={isKetqua ? "backward" : "forward"}
          days={7}
          onDateSelect={handleDateSelect}
        />
        <LeagueFilter
          compact
          leagues={filterLeagues}
          selectedIds={selectedIds}
          onToggle={handleLeagueToggle}
          onClear={() => setSelectedIds(null)}
          themeStyle={themeStyle}
          loading={loading}
        />
      </div>

      {loading ? <LtdLoading compact /> : null}

      {!loading ? (
        <>
          <div className="kbv-ltd-sb-list">
            {filteredMatches.length === 0 ? (
              <div className="kbv-kq-sb-empty">
                <NoData message="Không có trận đấu nào" />
              </div>
            ) : (
              filteredMatches.map(({ league, matches }, idx) => (
                <LeagueGroup
                  key={`${league?.id ?? league?.name ?? "lg"}-${idx}`}
                  league={league}
                  matches={matches}
                  render={(match, isLast) => (
                    <SidebarMatch key={match.id} match={match} isLastItem={isLast} />
                  )}
                />
              ))
            )}
          </div>
          {hasMore ? (
            <div ref={loadMoreRef} className="kbv-kq-sb-more">
              {isLoadingMore ? <LtdLoading compact /> : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
